import { Component, createRef } from 'react';
import { createPortal } from 'react-dom';
import { toPng } from 'html-to-image';

import logService from '../../../services/wss/logService3';
import umlCanvasEngine from '../../Core/UmlCanvasEngine';
import umlPreviewUpdater from '../../Core/Preview/UmlPreviewUpdater';
import { OpenToast } from '../../../JxControls/JxToasts/StyledToast';
import { emsToDisplay } from '../../../services/utils/DateFormat';
import { computeBounds } from './UmlWorldMapHelpers.jsx';
import { CANVAS_WORLD_SELECTOR, waitForPaint, computeFitTransform, prepareCloneForCapture } from './UmlPreviewDlgHelpers.js';
import './UmlPreviewDlg.css';

/**
 * UmlPreviewDlg — invisible off-screen capture host for the schema preview.
 * No backdrop, no visible UI — clones .uml-canvas-world into its own
 * captureRef, fit-to-bounds transform, captures via html-to-image, hands the
 * PNG to umlPreviewUpdater to save, shows a toast, then tells the caller to
 * close it.
 *
 * @param {boolean} open
 * @param {{rk: string, name: string, service: string}} note
 * @param {Function} onClose
 */
export default class UmlPreviewDlg extends Component {
    captureRef = createRef();

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.runCaptureAndSave();
        }
    }

    runCaptureAndSave = async () => {
        const { note, onClose } = this.props;
        let result = null;
        try {
            result = await this.captureAndSave();
        } catch (error) {
            logService.logError(`UmlPreviewDlg.runCaptureAndSave: ${error.message}`);
        }

        if (result) {
            OpenToast(`Preview "${note.name}" ${result.isUpdate ? 'updated' : 'created'}`, 'info');
        } else {
            OpenToast('Could not generate preview — see Output Log', 'error');
        }
        onClose?.();
    };

    /**
     * Clones the live canvas world INTO this.captureRef, applies a
     * fit-to-bounds transform to the clone only, captures it, then hands the
     * PNG to umlPreviewUpdater for saving. The live canvas is never touched.
     * @returns {Promise<{dataUrl: string, savedNote: object, isUpdate: boolean}|null>}
     */
    captureAndSave = async () => {
        const { note } = this.props;
        const liveWorld = document.querySelector(CANVAS_WORLD_SELECTOR);
        const container = this.captureRef.current;
        if (!note?.rk || !liveWorld || !container) {
            return null;
        }

        const bounds = computeBounds(umlCanvasEngine.shapes);
        const clone = liveWorld.cloneNode(true);
        clone.style.transform = computeFitTransform(bounds);
        prepareCloneForCapture(clone);

        const title = document.createElement('div');
        title.textContent = `${note.name ?? ''} - ${emsToDisplay(Date.now())}`;
        title.style.position = 'absolute';
        title.style.top = '8px';
        title.style.left = '0';
        title.style.width = '100%';
        title.style.textAlign = 'center';
        title.style.fontFamily = 'system-ui, "Segoe UI", Roboto, sans-serif';
        title.style.fontSize = '14px';
        title.style.fontWeight = '600';
        title.style.color = '#e8edf5';
        title.style.zIndex = '9999';

        container.innerHTML = '';
        container.appendChild(clone);
        container.appendChild(title);

        await waitForPaint();
        const dataUrl = await toPng(container, { skipFonts: true });

        return umlPreviewUpdater.save(note, dataUrl);
    };

    render() {
        if (!this.props.open) return null;

        return createPortal(
            <div className="upd-offscreen-host">
                <div className="upd-preview-frame" ref={this.captureRef} />
            </div>,
            document.body,
        );
    }
}
